import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MapPin, Search, ExternalLink, AlertCircle, Check, Loader } from 'lucide-react';
import apiClient from '../../lib/apiClient';

/*
 * AddressField — one box that takes whatever you have.
 *
 * The ask was "copy a Google address, paste it into a single line, and have
 * the application do the parsing — I don't want to deal with it at all". So
 * this is one input with two behaviours behind it, and the reader is never
 * asked which one they want:
 *
 *   TYPING  → a debounced type-ahead. The suggestions come from our own
 *             backend, not from Google's JS widget: that widget needs
 *             `script-src https://maps.googleapis.com` on a CSP that is
 *             deliberately `script-src 'self'`, and an API key sitting in the
 *             shipped bundle for anyone to read and spend.
 *
 *   PASTING → the whole string goes to /api/sites/lookup, which parses it.
 *             A Google "copy address" block, a maps.google.com link, a
 *             ?api=1 share link, or a plain line all work.
 *
 * ─── THE SESSION TOKEN IS THE BILL ───────────────────────────────────────────
 * Google charges autocomplete per KEYSTROKE unless every request in one typing
 * session carries the same token, in which case the session plus the details
 * call that ends it bill as a single unit. One token is minted per field, and
 * a NEW one after each selection, because a token is spent when it is used to
 * fetch details.
 *
 * ─── IT NEVER SILENTLY ACCEPTS A MATCH ───────────────────────────────────────
 * The batch resolver trusts a Places answer only within 200m of DGS's own
 * coordinate for the building; the answers it rejected sat at a median of
 * ~1.5km. A hand-entered site has no such coordinate, so `confidence:
 * 'unanchored'` means nothing independent agrees with this match and the
 * reader is told so, with a pin they can open. That notice is not decoration —
 * it is the whole reason a place ID entered this way can be trusted.
 */

const DEBOUNCE_MS = 250;
const MIN_CHARS = 3;

/** A session token is any opaque string Google has not seen before. */
function newSessionToken() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `s-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export default function AddressField({ onResolved, disabled }) {
  const [text, setText] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState([]);
  const [err, setErr] = useState(null);
  const [picked, setPicked] = useState(null);
  const [off, setOff] = useState(false);

  const token = useRef(newSessionToken());
  const timer = useRef(null);
  // Every request carries a sequence number so a slow early answer cannot
  // overwrite a fast later one — the classic type-ahead race.
  const seq = useRef(0);

  useEffect(() => () => clearTimeout(timer.current), []);

  const apply = useCallback((fields, meta) => {
    setPicked(meta || null);
    setSuggestions([]);
    onResolved(fields, meta);
  }, [onResolved]);

  /** The paste path: hand the whole string over and let the server parse it. */
  const lookupWhole = useCallback(async (value) => {
    setBusy(true);
    setErr(null);
    const mine = ++seq.current;
    try {
      const out = await apiClient.post('/api/sites/lookup', { text: value });
      if (mine !== seq.current) return;
      setNotes(out.notes || []);
      const p = out.parsed || {};
      apply({
        siteName: p.siteName || '',
        addressLine1: p.addressLine1 || '',
        addressLine2: p.addressLine2 || '',
        city: p.city || '',
        state: p.state || 'CA',
        postalCode: p.postalCode || '',
        googlePlaceId: out.best?.placeId || '',
      }, out.best ? { ...out.best, confidence: out.confidence } : null);
    } catch (e) {
      if (mine === seq.current) setErr(e?.data?.error || e.message || 'Could not read that address');
    } finally {
      if (mine === seq.current) setBusy(false);
    }
  }, [apply]);

  /** The typing path. */
  const suggest = useCallback(async (value) => {
    const mine = ++seq.current;
    try {
      const out = await apiClient.post('/api/sites/lookup/suggest', {
        input: value,
        sessionToken: token.current,
      });
      if (mine !== seq.current) return;
      setSuggestions(out.suggestions || []);
      // Places off is a normal state, not an error — the paste path still
      // works without a key because parsing is ours.
      if (out.placesEnabled === false) setOff(true);
    } catch (e) {
      if (mine === seq.current) setSuggestions([]);
    }
  }, []);

  const onChange = (e) => {
    const value = e.target.value;
    setText(value);
    setPicked(null);
    setErr(null);
    clearTimeout(timer.current);

    // A pasted line is long, or has commas, or is a URL. None of those are
    // worth spending keystroke suggestions on — parse the whole thing.
    const pasted = /^https?:\/\//i.test(value) || value.length > 60 || value.split(',').length > 2;
    if (!value.trim()) { setSuggestions([]); setNotes([]); return; }
    if (pasted) {
      timer.current = setTimeout(() => lookupWhole(value), DEBOUNCE_MS);
      return;
    }
    if (value.trim().length < MIN_CHARS || off) { setSuggestions([]); return; }
    timer.current = setTimeout(() => suggest(value), DEBOUNCE_MS);
  };

  const choose = async (s) => {
    setBusy(true);
    setErr(null);
    setSuggestions([]);
    setText(s.text);
    const mine = ++seq.current;
    try {
      const out = await apiClient.post('/api/sites/lookup/select', {
        placeId: s.placeId,
        sessionToken: token.current,
      });
      if (mine !== seq.current) return;
      const p = out.parsed || {};
      apply({
        siteName: out.name || p.siteName || '',
        addressLine1: p.addressLine1 || '',
        addressLine2: p.addressLine2 || '',
        city: p.city || '',
        state: p.state || 'CA',
        postalCode: p.postalCode || '',
        googlePlaceId: out.placeId || '',
      }, {
        placeId: out.placeId,
        name: out.name,
        formattedAddress: out.formattedAddress,
        mapsUrl: out.mapsUrl,
        // The reader chose this row out of a list with their own eyes, which
        // is a stronger signal than any distance check we could run.
        confidence: 'picked',
      });
      setNotes([]);
    } catch (e) {
      if (mine === seq.current) setErr(e?.data?.error || e.message || 'Could not read that place');
    } finally {
      // Spent. The next lookup is a new session and must not reuse it.
      token.current = newSessionToken();
      if (mine === seq.current) setBusy(false);
    }
  };

  return (
    <div className="sites-addr">
      <label className="sites-addr-label" htmlFor="sites-addr-input">
        Address
        <span className="sites-muted"> — type it, or paste anything from Google Maps</span>
      </label>
      <div className="sites-addr-box">
        {busy ? <Loader size={14} className="sites-addr-spin" aria-hidden />
          : <Search size={14} aria-hidden />}
        <input
          id="sites-addr-input"
          value={text}
          onChange={onChange}
          disabled={disabled}
          autoComplete="off"
          spellCheck={false}
          placeholder="450 N St, Sacramento, CA 95814  ·  or a maps.google.com link"
        />
      </div>

      {suggestions.length > 0 && (
        <ul className="sites-addr-suggest">
          {suggestions.map((s) => (
            <li key={s.placeId}>
              <button type="button" onClick={() => choose(s)}>
                <MapPin size={13} aria-hidden />
                <span className="sites-addr-main">{s.mainText || s.text}</span>
                {s.secondaryText && <span className="sites-muted"> {s.secondaryText}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}

      {picked && (
        <div className={`sites-addr-picked${picked.confidence === 'unanchored' ? ' sites-addr-unsure' : ''}`}>
          {picked.confidence === 'unanchored'
            ? <AlertCircle size={13} aria-hidden />
            : <Check size={13} aria-hidden />}
          <span>
            {picked.confidence === 'unanchored'
              ? 'Google found a match, but nothing independent confirms it is the right building.'
              : 'Matched to a Google listing.'}
          </span>
          {picked.mapsUrl && (
            <a href={picked.mapsUrl} target="_blank" rel="noreferrer">
              Open the pin <ExternalLink size={11} aria-hidden />
            </a>
          )}
        </div>
      )}

      {notes.map((n) => (
        <p key={n} className="sites-muted sites-addr-note">{n}</p>
      ))}
      {err && <div className="accounts-error"><AlertCircle size={13} aria-hidden /> {err}</div>}
      {off && (
        <p className="sites-muted sites-addr-note">
          Address suggestions are off (no Google Places key configured) — pasting a full
          address still works, the parsing is ours.
        </p>
      )}
    </div>
  );
}
